import React, { useState, useEffect } from 'react';
import './CommitteeMembers.css';
import API_BASE_URL from '../config/api';

const sectionOrder = [
  "Parish Committee Members",
  "Organization Representatives",
  "Prayer Group Representatives",
  "Members of Assembly and Mandalam",
  "Internal Auditors"
];

const CommitteeMembers = () => {
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [addingSection, setAddingSection] = useState(null);
  const [newMemberData, setNewMemberData] = useState({ title: '', name: '', phone: '', priority: '' });

  const handleAddClick = (sectionName) => {
    setAddingSection(sectionName);
  };

  const handleCancelAdd = () => {
    setAddingSection(null);
    setNewMemberData({ title: '', name: '', phone: '', priority: '' });
  };

  const handleNewMemberChange = (e) => {
    const { name, value } = e.target;
    setNewMemberData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddNewMember = async () => {
    if (!newMemberData.name || !newMemberData.title || !newMemberData.priority) {
      setError("Title, Name, and Priority are required.");
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/committee-members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ ...newMemberData, section: addingSection })
      });

      if (!response.ok) {
        throw new Error('Failed to add member');
      }

      handleCancelAdd();
      fetchMembers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveClick = async () => {
    if (!editingData) return;

    // Find the section name for the member being edited
    let sectionName = '';
    for (const section of sectionOrder) {
      if (members[section]?.some(member => member.id === editingId)) {
        sectionName = section;
        break;
      }
    }

    if (!sectionName) {
      setError("Could not determine the member's section. Please try again.");
      return;
    }

    const payload = { ...editingData, section: sectionName };

    try {
      const response = await fetch(`${API_BASE_URL}/api/committee-members/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to save member');
      }

      setEditingId(null);
      setEditingData(null);
      fetchMembers(); // Refetch to get updated and sorted list
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteClick = async (memberId) => {
    if (window.confirm('Are you sure you want to delete this member?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/committee-members/${memberId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete member');
        }

        fetchMembers(); // Refetch to update list
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleEditClick = (member) => {
    setEditingId(member.id);
    setEditingData({ ...member });
  };

  const handleCancelClick = () => {
    setEditingId(null);
    setEditingData(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingData(prev => ({ ...prev, [name]: value }));
  };

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/committee-members`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch committee members');
      }
      const data = await response.json();
      setMembers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  if (loading) {
    return <div className="loading">Loading Committee Members...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="committee-members-container">
      {sectionOrder.map(sectionName => (
        <div key={sectionName} className="committee-section">
          <h3>{sectionName}</h3>
          <table className="committee-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Priority</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members[sectionName]?.map(member => {
                const isEditing = editingId === member.id;
                return (
                  <tr key={member.id}>
                    <td>{isEditing ? <input type="text" name="title" value={editingData.title} onChange={handleEditChange} /> : member.title}</td>
                    <td>{isEditing ? <input type="text" name="name" value={editingData.name} onChange={handleEditChange} /> : member.name}</td>
                    <td>{isEditing ? <input type="text" name="phone" value={editingData.phone} onChange={handleEditChange} /> : member.phone}</td>
                    <td>{isEditing ? <input type="number" name="priority" value={editingData.priority} onChange={handleEditChange} min="1" /> : member.priority}</td>
                    <td>
                      {isEditing ? (
                        <>
                          <button className="action-btn save" onClick={handleSaveClick}>💾</button>
                          <button className="action-btn cancel" onClick={handleCancelClick}>❌</button>
                        </>
                      ) : (
                        <>
                          <button className="action-btn edit" onClick={() => handleEditClick(member)}>✏️</button>
                          <button className="action-btn delete" onClick={() => handleDeleteClick(member.id)}>🗑️</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {addingSection === sectionName && (
                <tr className="add-new-row">
                  <td><input type="text" name="title" placeholder="Title" value={newMemberData.title} onChange={handleNewMemberChange} /></td>
                  <td><input type="text" name="name" placeholder="Name" value={newMemberData.name} onChange={handleNewMemberChange} /></td>
                  <td><input type="text" name="phone" placeholder="Phone" value={newMemberData.phone} onChange={handleNewMemberChange} /></td>
                  <td><input type="number" name="priority" placeholder="Priority" value={newMemberData.priority} onChange={handleNewMemberChange} min="1" /></td>
                  <td>
                    <button className="action-btn save" onClick={handleAddNewMember}>💾</button>
                    <button className="action-btn cancel" onClick={handleCancelAdd}>❌</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="section-footer">
            <button className="add-btn" onClick={() => handleAddClick(sectionName)}>+ Add</button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CommitteeMembers;
